import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";

const collection = getCollection("rio-grande-series")!;

const content: FlagshipContent = {
  eyebrow: "Signature Collection · Hospitality & Resorts",
  heroKicker: "Hospitality & Resorts · San Antonio · Austin · Hill Country · Corpus Christi · South Padre Island",
  badgeSubline:
    "Hotels • Resorts • Glamping • Safari Tents • Spas • Retreats • Event Venues",
  lifestyleIntro:
    "Poolside cabanas, safari tents under the live oaks, spa courtyards, patio dinners after sundown — outdoor solutions for the entire Texas hospitality industry.",
  lifestyleBody:
    "The Rio Grande Series is Texas Made Shade's hospitality division: hotels and resorts, glamping and safari tents, spas and wellness decks, vacation and ranch retreats, event venues, boutique lodging, and restaurant patios. We don't sell one product — we build guest-experience shade that survives coastal wind, salt exposure, Hill Country sun, and the constant use hospitality properties put on every piece of fabric.",
  solutionsHeading: "Outdoor solutions for the hospitality industry",
  solutions: [
    { title: "Resort & Hotel Cabanas", desc: "Custom poolside cabanas built for coastal sun, salt, and wind.", to: "/custom-awnings" },
    { title: "Glamping & Safari Tents", desc: "Canvas safari tents, glamping structures, and deck canopies for hotels, resorts, safari camps and retreats.", to: "/safari-glamping-tents" },
    { title: "Spas & Wellness Shade", desc: "Shade for spa courtyards, massage decks, cold plunge and treatment patios.", to: "/pool-spa" },
    { title: "Vacation & Ranch Retreats", desc: "Lodge porches, guest-cabin awnings, and gathering-space shade for retreat properties.", to: "/custom-fabrication" },
    { title: "Event & Venue Shade", desc: "Sail shades and canopies for wedding lawns, ceremony spaces, and courtyards.", to: "/sail-shades" },
    { title: "Boutique Lodging Awnings", desc: "Entry awnings, window canopies, and branded storefront shade for small hotels and inns.", to: "/commercial-awnings" },
    { title: "Restaurant & Hospitality Patios", desc: "Drop curtains, awnings, and covers for hotel and resort dining patios.", to: "/restaurant-awnings" },
    { title: "Poolside Drop Curtains", desc: "Weatherproof canvas curtains for cabanas, pool decks, and cabana bars.", to: "/curtains" },
    { title: "Motorized Retractables", desc: "Solair® retractables and drop screens for guest-facing patios.", to: "/retractable-awnings" },
    { title: "Recover & Refresh", desc: "Re-canvas aging hospitality awnings, tents, and cabanas without replacing frames.", to: "/recover-awnings" },
  ],
  trustBadges: [
    "Coastal-grade Sunbrella® canvas",
    "Welded aluminum frames",
    "Glamping & safari tent fabrication",
    "Portfolio recover programs",
  ],
  faqs: [
    {
      q: "Can you handle coastal salt and wind exposure?",
      a: "Yes — Rio Grande Series builds use marine-grade hardware and coastal-rated Sunbrella® canvas engineered for Gulf Coast conditions.",
    },
    {
      q: "Do you build for hotel portfolios and management groups?",
      a: "Yes. We handle single-property refreshes and multi-property recover programs across San Antonio, Austin, Corpus Christi, and South Padre Island.",
    },
    {
      q: "How fast can you turn around a recover?",
      a: "Recover timelines depend on canvas selection and site access — most single-property recovers ship and install inside a few weeks. Send photos and dimensions for a firm quote.",
    },
    {
      q: "Can you work around guest occupancy and event calendars?",
      a: "Yes — hospitality installs are almost always scheduled around occupancy, ballroom bookings, and event calendars. Send your restrictions with the project brief.",
    },
    {
      q: "Do you build glamping and safari tents?",
      a: "Yes. Glamping and safari tents live inside the Rio Grande Series™ because they're a hospitality product. We fabricate canvas safari tents, glamping wall tents, deck canopies, and replacement tops for ranch retreats, RV resorts, and destination properties across Texas.",
    },
    {
      q: "What kinds of properties does Rio Grande cover?",
      a: "Hotels and resorts, glamping and safari tent camps, spas and wellness centers, vacation and ranch retreats, event venues, boutique lodging, and restaurant and hospitality patios — the full commercial guest-experience market.",
    },
  ],
};

export const Route = createFileRoute("/rio-grande-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Rio Grande Series™ — Hospitality, Resorts & Glamping | Texas Made Shade",
      description:
        "Rio Grande Series™ — resort cabanas, glamping and safari tents, spa shade, hotel patio awnings and event-venue structures. San Antonio, Austin and statewide Texas.",
      keywords:
        "resort cabanas texas, glamping tents texas, safari tents, spa and wellness shade, ranch retreat lodging, boutique hotel awnings, hotel patio awnings, restaurant patio enclosures, event venue shade, poolside drop curtains, hospitality awnings texas",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Rio Grande Series Hospitality & Resort Shade",
      serviceType: "Hotels, Resorts, Glamping & Safari Tents, Spas, Retreats & Event Venues",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Rio Grande Series — Hospitality & Resorts",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
